import { Controller } from "@hotwired/stimulus"

// Controlador para gestionar menús desplegables.
// Cierra automáticamente otros menús al abrir uno nuevo y se cierra al hacer clic fuera.
// Soporta carga bajo demanda (lazy loading) de contenido de menú.
export default class extends Controller {
  static targets = ["menu"]
  static values = {
    menuUrl: String,      // URL para cargar el menú (ej: /apps/home-repairs/tickets/123/dropdown_menu?menu_type=pipeline)
    loaded: { type: Boolean, default: false }  // Si ya se cargó el contenido
  }

  connect() {
    this.closeAllListener = this.closeIfOther.bind(this)
    document.addEventListener("dropdown:close-all", this.closeAllListener)
  }

  disconnect() {
    document.removeEventListener("dropdown:close-all", this.closeAllListener)
    this.close()
  }

  toggle(event) {
    event.stopPropagation()

    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  async open() {
    document.dispatchEvent(new CustomEvent("dropdown:close-all", { detail: { sender: this.element } }))

    // Si tiene URL y no está cargado, hacer fetch
    if (this.hasMenuUrlValue && !this.loadedValue) {
      await this.loadMenuContent()
    }

    // Cambiar a position fixed y posicionar relativo al trigger
    this.menuTarget.style.position = 'fixed'
    this.positionMenu()

    this.menuTarget.classList.remove("hidden")
    this.element.classList.add("dropdown-open")

    // Reposicionar en scroll
    this.scrollListener = () => this.positionMenu()
    window.addEventListener('scroll', this.scrollListener, true)

    setTimeout(() => {
      this.closeOnClickOutsideListener = this.closeOnClickOutside.bind(this)
      document.addEventListener("pointerdown", this.closeOnClickOutsideListener, true)
      document.addEventListener("click", this.closeOnClickOutsideListener, true)
    }, 0)
  }

  positionMenu() {
    const trigger = this.element.querySelector('[data-action*="dropdown#toggle"]')
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    this.menuTarget.style.top = `${rect.bottom + 4}px` // 4px = mt-1
    this.menuTarget.style.left = `${rect.left}px`
  }

  async loadMenuContent() {
    try {
      // Mostrar loading
      this.menuTarget.innerHTML = '<div class="text-[10px] text-gray-400 px-2 py-1">Cargando...</div>'
      
      const response = await fetch(this.menuUrlValue, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        this.menuTarget.innerHTML = html
        this.loadedValue = true
      } else {
        this.menuTarget.innerHTML = '<div class="text-[10px] text-red-500 px-2 py-1">Error al cargar</div>'
      }
    } catch (error) {
      console.error('Error loading dropdown menu:', error)
      this.menuTarget.innerHTML = '<div class="text-[10px] text-red-500 px-2 py-1">Error al cargar</div>'
    }
  }

  close() {
    if (!this.hasMenuTarget || this.menuTarget.classList.contains("hidden")) {
      return
    }
    this.menuTarget.classList.add("hidden")
    this.element.classList.remove("dropdown-open")
    
    // Limpiar listener de scroll
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true)
      this.scrollListener = null
    }
    
    if (this.closeOnClickOutsideListener) {
      document.removeEventListener("pointerdown", this.closeOnClickOutsideListener, true)
      document.removeEventListener("click", this.closeOnClickOutsideListener, true)
      this.closeOnClickOutsideListener = null
    }
  }

  closeIfOther(event) {
    if (event.detail.sender !== this.element) {
      this.close()
    }
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  hide(event) {
    event.preventDefault()
    this.close()
  }
}
